import React, { useEffect, useMemo, useCallback } from 'react';
import { Box, Card, Typography, Table, TableHead, TableRow, TableCell, TableBody, TableContainer, Button, Link, Divider } from '@mui/material';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { ReactComponent as ViewIcon } from '../../../../assets/SVG/eye_view.svg';
import { ReactComponent as Refund } from '../../../../assets/SVG/tick-circle-1.svg';
import HeaderProfile from '../../../../components/header_Profile/header_Profile.index';
import SummaryCard from './SummaryCard';
import { useNavigate, useLocation } from 'react-router-dom';
import { viewInvoiceData } from './viewInvoiceData';
import Grid2 from '@mui/material/Grid2';
import { useInvoiceDetails, useBillingConfigOptions } from './newInvoiceForm.helper';
import useCurrency from '../../../../components/CurrencyProvider';
import { LABEL_SX, BODY_SX, BODY_MULTILINE_SX, TABLE_HEADER_ROW_SX } from '../../../../utils/typographyStyles';
import { formatDateDMY } from '../../../../utils/dateUtils';

// ============================================================================
// Reusable Section Component
// ============================================================================

interface InfoSectionProps {
  label: string;
  value: string;
  multiline?: boolean;
}

const InfoSection: React.FC<InfoSectionProps> = ({ label, value, multiline = false }) => (
  <Box sx={{ mb: 2.5 }}>
    <Typography sx={LABEL_SX}>{label}</Typography>
    <Typography sx={multiline ? BODY_MULTILINE_SX : BODY_SX}>{value}</Typography>
  </Box>
);

const ViewInvoice = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const invoiceId = (location.state as { invoice_id?: number | string | null } | null)?.invoice_id ?? null;
  const { formatCurrency } = useCurrency();

  // Minimal API call just to log the response; UI remains unchanged
  const {
    data: invoiceDetails,
    error: invoiceError,
    isLoading: invoiceLoading,
  } = useInvoiceDetails(invoiceId ?? undefined);

  useEffect(() => {
    if (invoiceDetails) {
      // eslint-disable-next-line no-console
      console.log('ViewInvoice: invoice details response', invoiceDetails);
    }
  }, [invoiceDetails]);

  useEffect(() => {
    if (invoiceError) {
      // eslint-disable-next-line no-console 
      console.error('ViewInvoice: invoice details error', invoiceError);
    }
  }, [invoiceError]);

  // Fetch billing config options to map concession reason code to name
  const { data: billingConfigData } = useBillingConfigOptions(invoiceDetails?.patient);

  // Helper to map concession reason code to name
  const getConcessionReasonName = useCallback((code: string | null | undefined): string => {
    if (!code || code === '-') return '-';
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const configData = billingConfigData as any;
    const concessionReasons = configData?.CONCESSION_REASON || [];
    const found = concessionReasons.find((item: { code: string; name: string }) => item.code === code);
    return found?.name || code;
  }, [billingConfigData]);

  // Transform API data or fallback to mock data
  const {
    billNo,
    patientId,
    services,
    totals: { totalAmount, concessionAmount, taxAmount, netAmount },
    meta: { concessionReason, dueDate, termsAndConditions, legalMentions, remark, comments },
  } = useMemo(() => {
    if (!invoiceDetails) {
      return viewInvoiceData; // fallback to mock
    }

    // Map API services to the shape expected by the table
    const mappedServices = invoiceDetails.services.map((svc) => ({
      date: svc.date,
      code: svc.billing_source_code,
      package: svc.package_name ?? '-',
      service: svc.billing_source_name,
      provider: svc.provider_name ?? (svc.provider != null ? String(svc.provider) : '-'),
      charges: Number(svc.charges) || 0,
      concessionAmt: Number(svc.concession_amount) || 0,
      concessionPercent: Number(svc.concession_percentage) || 0,
      taxAmt: Number(svc.tax_amount) || 0,
      taxPercent: Number(svc.tax_percentage) || 0,
      netAmt: Number(svc.net_amount) || 0,
      refundStatus: svc.is_refunded ? 'refunded' : 'success',
    }));

    return {
      billNo: invoiceDetails.bill_number ?? String(invoiceDetails.id),
      patientId: invoiceDetails.patient,
      services: mappedServices,
      totals: {
        totalAmount: Number(invoiceDetails.total_amount) || 0,
        concessionAmount: Number(invoiceDetails.concession_amount) || 0,
        taxAmount: Number(invoiceDetails.tax_amount) || 0,
        netAmount: Number(invoiceDetails.net_amount) || 0,
      },
      meta: {
        concessionReason: getConcessionReasonName(invoiceDetails.concession_reason),
        dueDate: invoiceDetails.due_date ?? '-',
        termsAndConditions: invoiceDetails.terms_and_conditions ?? '',
        legalMentions: invoiceDetails.legal_mentions ?? '',
        remark: invoiceDetails.remark ?? '',
        comments: invoiceDetails.comments ?? '',
      },
    };
  }, [invoiceDetails, getConcessionReasonName]);

  // Show loading state if fetching
  if (invoiceLoading) {
    return (
      <>
        <HeaderProfile />
        <Box sx={{ mx: 2, mt: 3, mb: 3, textAlign: 'center' }}>
          <Typography>Loading invoice details...</Typography>
        </Box>
      </>
    );
  }

  return (
    <>
      <HeaderProfile />

      <Box sx={{ mx: 2, mt: 3, mb: 3 }}>
        {/* Top services header card */}
        <Card
          elevation={0}
          sx={{
            p: 2,
            borderRadius: 3,
            border: '1px solid #E4E7EC',
            backgroundColor: '#FFFFFF',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 1.5,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
              <Typography
                sx={{
                  fontFamily: 'Montserrat, sans-serif',
                  fontWeight: 700,
                  fontSize: 20,
                  lineHeight: 1,
                  color: '#232323',
                }}
              >
                Services
              </Typography>
              <Typography
                component="span"
                sx={{
                  fontFamily: 'Montserrat, sans-serif',
                  fontWeight: 500,
                  fontSize: 16,
                  lineHeight: '22px',
                  color: '#232323',
                }}
              >
                (Bill No. - {billNo})
              </Typography>
            </Box>

            <Link
              component="button"
              type="button"
              underline="none"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                fontFamily: 'Montserrat, sans-serif',
                fontWeight: 700,
                fontSize: 16,
                lineHeight: '22px',
                color: '#5A8AEA',
              }}
              onClick={() => {
                // TODO: Temporarily using patient-log route because company-related logs API is not available yet.
                // Once company logs are implemented, update to use the appropriate route based on invoice type.
                // navigate(`/admin/billing/invoice/log/${patientId}`, { state: { invoice_id: invoiceId } });
                navigate(`/admin/billing/invoice/patient-log/${patientId}`, { state: { invoice_id: invoiceId } });                
              }}
            >
              <ViewIcon style={{ width: 20, height: 20 }} />
              View Invoice Logs
            </Link>
          </Box>

          <TableContainer sx={{ maxHeight: { xs: 320, md: 480 }, overflowX: 'auto' }}>
            <Table sx={{ borderCollapse: 'collapse', minWidth: 800 }}>
              <TableHead>
                <TableRow sx={TABLE_HEADER_ROW_SX}>
                  <TableCell sx={{ borderTopLeftRadius: 12, borderBottomLeftRadius: 12 }}>
                    Date
                  </TableCell>
                  <TableCell>Code</TableCell>
                  <TableCell>Package</TableCell>
                  <TableCell>Service</TableCell>
                  <TableCell>Provider</TableCell>
                  <TableCell align="right">Charges</TableCell>
                  <TableCell align="right">Concession Amt. | %</TableCell>
                  <TableCell align="right">Tax Amt. | %</TableCell>
                  <TableCell align="right">Net Amt.</TableCell>
                  <TableCell
                    align="center"
                    sx={{ borderTopRightRadius: 12, borderBottomRightRadius: 12 }}
                  >
                    Refund Status
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {services.map((svc, idx) => (
                  <TableRow
                    key={idx}
                    sx={{
                      background: idx % 2 === 0 ? '#FFFFFF' : '#FAFBFC',
                      transition: 'background 120ms ease',
                      '&:hover': { background: '#F4F6F8' },
                      '& td': {
                        borderTop: idx === 0 ? 'none' : undefined,
                        borderBottom: idx === services.length - 1 ? 'none' : undefined,
                      },
                    }}
                  >
                    <TableCell>{svc.date ? formatDateDMY(svc.date) : '-'}</TableCell>
                    <TableCell>{svc.code}</TableCell>
                    <TableCell>{svc.package}</TableCell>
                    <TableCell>
                      {/* Render service with title and optional subtext */}
                      {(() => {
                        const match = svc.service.match(/^(.*?)\s*\((.*)\)\s*$/);
                        const title = match ? match[1] : svc.service;
                        const sub = match ? match[2] : '';
                        return (
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {title}
                            </Typography>
                            {sub && (
                              <Typography variant="caption" color="text.secondary">
                                ({sub})
                              </Typography>
                            )}
                          </Box>
                        );
                      })()}
                    </TableCell>
                    <TableCell>{svc.provider}</TableCell>
                    <TableCell align="right">{formatCurrency(svc.charges)}</TableCell>
                    <TableCell align="right">
                      {formatCurrency(svc.concessionAmt)} | {svc.concessionPercent.toFixed(2)}%
                    </TableCell>
                    <TableCell align="right">
                      {formatCurrency(svc.taxAmt)} | {svc.taxPercent.toFixed(2)}%
                    </TableCell>
                    <TableCell align="right">{formatCurrency(svc.netAmt)}</TableCell>
                    <TableCell align="center">
                      {svc.refundStatus === 'refunded' ? (
                        <Refund width={32} height={32}/>
                      ) : (
                        <RemoveCircleOutlineIcon sx={{ color: '#bdbdbd', fontSize: 24 }} />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>

        {/* Bottom section: left details + right summary */}
        <Grid2 container spacing={3} sx={{ mt: 3, mx: 2 }}>
          <Grid2 size={{ xs: 12, md: 8 }}>
            {/* Concession Reason & Due Date side by side */}
            <Box sx={{ display: 'flex', gap: 12, mb: 3 }}>
              <Box>
                <Typography sx={LABEL_SX}>Concession Reason</Typography>
                <Typography sx={BODY_SX}>{concessionReason || '-'}</Typography>
              </Box>
              <Box>
                <Typography sx={LABEL_SX}>Due Date</Typography>
                <Typography sx={BODY_SX}>{dueDate && dueDate !== '-' ? formatDateDMY(dueDate) : '-'}</Typography>
              </Box>
            </Box>

            {termsAndConditions && <InfoSection label="Terms and conditions" value={termsAndConditions} multiline />}
            {legalMentions && <InfoSection label="Legal Mentions" value={legalMentions} multiline />}
            {remark && <InfoSection label="Remark" value={remark} multiline />}
            {comments && <InfoSection label="Comments" value={comments} multiline />}
          </Grid2>

          <Grid2 size={{ xs: 12, md: 4 }}>
            <SummaryCard
              total={totalAmount}
              concession={concessionAmount}
              tax={taxAmount}
              net={netAmount}
            />
          </Grid2>
        </Grid2>

        <Divider sx={{ mt: 3, mb: 2 }} />

        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            color="secondary"
            onClick={() => {
              // navigate back to invoice list page
              navigate('/admin/billing/invoice/patient-invoice' + (patientId ? '/' + patientId : ''));
            }}
          >
            Back to Invoice List
          </Button>
        </Box>
      </Box>
    </>
  );
};

export default ViewInvoice; 
